<template>
  <div class="category-panel">
    <div class="category-header">
      <span>分类管理</span>
      <el-button type="text" icon="el-icon-plus" @click="handleAddRoot">新增</el-button>
    </div>
    <el-input
      v-model="keyword"
      size="small"
      clearable
      prefix-icon="el-icon-search"
      placeholder="请输入分类名称"
      class="category-search"
      @input="handleFilter"
    />
    <div class="category-tree" v-loading="loading">
      <div
        class="all-category"
        :class="{ active: selectedId === null }"
        @click="handleSelectAll"
      >
        <i class="el-icon-menu"></i>
        <span>全部分类</span>
      </div>
      <el-tree
        ref="categoryTree"
        :data="treeData"
        node-key="id"
        :props="{ label: 'categoryName', children: 'children' }"
        default-expand-all
        highlight-current
        :expand-on-click-node="false"
        :filter-node-method="filterNode"
        @node-click="handleNodeClick"
      >
        <span class="category-node" slot-scope="{ node, data }">
          <span class="node-label" :class="{ disabled: data.status === 0 }" :title="node.label">
            {{ node.label }}
          </span>
          <span class="node-actions">
            <el-tooltip content="新增子分类" placement="top">
              <i class="el-icon-plus" @click.stop="handleAddChild(data)"></i>
            </el-tooltip>
            <el-tooltip content="编辑" placement="top">
              <i class="el-icon-edit" @click.stop="handleEdit(data)"></i>
            </el-tooltip>
            <el-tooltip content="删除" placement="top">
              <i class="el-icon-delete" @click.stop="handleDelete(data)"></i>
            </el-tooltip>
          </span>
        </span>
      </el-tree>
      <el-empty v-if="!loading && treeData.length === 0" description="暂无分类" :image-size="60" />
    </div>

    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="480px"
      append-to-body
      :close-on-click-modal="false"
      @closed="resetForm"
    >
      <el-form ref="categoryForm" :model="form" :rules="rules" label-width="90px">
        <el-form-item label="上级分类">
          <treeselect
            v-model="form.parentId"
            :options="parentOptions"
            :normalizer="normalizer"
            placeholder="顶级分类"
            :clearable="true"
            :default-expand-level="2"
          />
        </el-form-item>
        <el-form-item label="分类名称" prop="categoryName">
          <el-input v-model.trim="form.categoryName" maxlength="50" show-word-limit placeholder="请输入分类名称" />
        </el-form-item>
        <el-form-item label="排序号" prop="sortOrder">
          <el-input-number v-model="form.sortOrder" :min="0" :max="9999" controls-position="right" />
        </el-form-item>
        <el-form-item label="状态">
          <el-radio-group v-model="form.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model.trim="form.remark" type="textarea" :rows="3" maxlength="200" show-word-limit placeholder="请输入备注" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="submitting" @click="handleSubmit">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import Treeselect from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'
import {
  addCourseCategory,
  updateCourseCategory,
  deleteCourseCategory,
} from '@/api/video'

export default {
  name: 'CourseCategoryManage',
  components: { Treeselect },
  props: {
    categories: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      keyword: '',
      selectedId: null,
      dialogVisible: false,
      submitting: false,
      editingId: null,
      form: this.getEmptyForm(),
      rules: {
        categoryName: [{ required: true, message: '请输入分类名称', trigger: 'blur' }],
      },
      normalizer(node) {
        return {
          id: node.id,
          label: node.categoryName,
          children: node.children,
        }
      },
    }
  },
  computed: {
    treeData() {
      return this.buildTree(this.categories)
    },
    dialogTitle() {
      return this.editingId ? '编辑课程分类' : '新增课程分类'
    },
    parentOptions() {
      return this.removeNode(this.treeData, this.editingId)
    },
  },
  methods: {
    getEmptyForm() {
      return {
        categoryName: '',
        parentId: null,
        sortOrder: 0,
        status: 1,
        remark: '',
      }
    },
    buildTree(list) {
      const map = {}
      const tree = []
      // 统一把 id / parentId 转成字符串，避免 JS Map 键类型不一致导致父节点找不到
      list.forEach(item => {
        map[String(item.id)] = { ...item, id: String(item.id) }
      })
      list.forEach(item => {
        const node = map[String(item.id)]
        const pid = item.parentId == null ? '' : String(item.parentId)
        if (!pid || !map[pid]) {
          tree.push(node)
        } else {
          const parent = map[pid]
          if (!parent.children) this.$set(parent, 'children', [])
          parent.children.push(node)
        }
      })
      return tree
    },
    removeNode(nodes, id) {
      return nodes.reduce((result, node) => {
        if (node.id === id) return result
        const copy = { ...node }
        if (copy.children) copy.children = this.removeNode(copy.children, id)
        result.push(copy)
        return result
      }, [])
    },
    handleFilter(value) {
      this.$refs.categoryTree && this.$refs.categoryTree.filter(value)
    },
    filterNode(value, data) {
      if (!value) return true
      return data.categoryName.indexOf(value) !== -1
    },
    handleSelectAll() {
      this.selectedId = null
      this.$refs.categoryTree && this.$refs.categoryTree.setCurrentKey(null)
      this.$emit('select', null)
    },
    handleNodeClick(data) {
      this.selectedId = data.id
      this.$emit('select', data.id)
    },
    handleAddRoot() {
      this.openDialog(null)
    },
    handleAddChild(data) {
      this.openDialog(null, data.id)
    },
    handleEdit(data) {
      this.editingId = data.id
      this.form = {
        categoryName: data.categoryName || '',
        parentId: data.parentId || null,
        sortOrder: data.sortOrder || 0,
        status: data.status == null ? 1 : data.status,
        remark: data.remark || '',
      }
      this.dialogVisible = true
    },
    openDialog(id, parentId) {
      this.editingId = id
      this.form = this.getEmptyForm()
      this.form.parentId = parentId || null
      this.dialogVisible = true
    },
    resetForm() {
      this.editingId = null
      this.form = this.getEmptyForm()
      this.$refs.categoryForm && this.$refs.categoryForm.clearValidate()
    },
    handleSubmit() {
      this.$refs.categoryForm.validate(async valid => {
        if (!valid) return
        this.submitting = true
        try {
          const data = {
            ...this.form,
            parentId: this.form.parentId || 0,
          }
          const res = this.editingId
            ? await updateCourseCategory({ ...data, id: this.editingId })
            : await addCourseCategory(data)
          if (res.code === 200 && res.data) {
            this.$message.success(this.editingId ? '修改成功' : '新增成功')
            this.dialogVisible = false
            this.$emit('changed')
          }
        } catch (e) {
          this.$message.error('保存分类失败')
        } finally {
          this.submitting = false
        }
      })
    },
    async handleDelete(data) {
      try {
        await this.$confirm(`删除后分类将被禁用，确定删除“${data.categoryName}”吗？`, '提示', {
          type: 'warning',
        })
        const res = await deleteCourseCategory(data.id)
        if (res.code === 200 && res.data) {
          this.$message.success('删除成功')
          if (this.selectedId === data.id) this.handleSelectAll()
          this.$emit('changed')
        }
      } catch (e) {
        // 取消操作
      }
    },
  },
}
</script>

<style scoped lang="scss">
.category-panel {
  width: 250px;
  min-width: 250px;
  height: 100%;
  min-height: 0;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 14px 12px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}
.category-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 30px;
  color: #303133;
  font-size: 15px;
  font-weight: 600;
}
.category-search {
  margin: 8px 0 12px;
}
.category-tree {
  flex: 1;
  min-height: 300px;
  overflow: auto;
}
.all-category {
  height: 34px;
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 0 8px;
  border-radius: 3px;
  font-size: 13px;
  cursor: pointer;
  color: #606266;
  &.active,
  &:hover {
    background: #ecf5ff;
    color: #409eff;
  }
}
.category-node {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: calc(100% - 8px);
  min-width: 0;
  font-size: 13px;
  .node-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    &.disabled {
      color: #c0c4cc;
      text-decoration: line-through;
    }
  }
  .node-actions {
    display: none;
    flex-shrink: 0;
    margin-left: 4px;
    color: #909399;
    i {
      margin-left: 5px;
      &:hover {
        color: #409eff;
      }
      &.el-icon-delete:hover {
        color: #f56c6c;
      }
    }
  }
  &:hover .node-actions {
    display: inline-flex;
  }
}
::v-deep .el-tree-node__content {
  height: 34px;
  border-radius: 3px;
}
::v-deep .el-tree-node:focus > .el-tree-node__content {
  background: #ecf5ff;
}
</style>
